import { useTranslation } from '@/i18n';
import {
  IconExternalLink,
  LinkButton,
  IconDownload,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import { PAYMENT_REQUEST_IMPORT_SUPPORT_URL } from '../../../constants';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

type ImportInvoiceReportSampleCsvPath = {
  enabled?: boolean;
  utf_8?: string;
  shift_jis?: string;
};

type OfficeInvoiceReportSettingsImportInvoiceReportSampleCsvPaths = {
  instant_payee?: ImportInvoiceReportSampleCsvPath;
  payee?: ImportInvoiceReportSampleCsvPath;
};

export type DetailDrawerContentProps = {
  /** Sample CSV download URLs from API settings (payee + instant_payee, utf_8 + shift_jis). Used as normal href. */
  importInvoiceReportSampleCsvPaths?: OfficeInvoiceReportSettingsImportInvoiceReportSampleCsvPaths;
};

export const DetailDrawerContent: FC<DetailDrawerContentProps> = memo(
  ({ importInvoiceReportSampleCsvPaths }) => {
    const { t } = useTranslation();
    const payee = importInvoiceReportSampleCsvPaths?.payee;
    const instantPayee = importInvoiceReportSampleCsvPaths?.instant_payee;

    return (
      <div className={cx('container')}>
        <div className={cx('title-container')}>
          <div className={cx('title')}>
            {t('invoice_reports.list_import_drawer.sample_title')}
          </div>
          <div className={cx('sub-title')}>
            {t('invoice_reports.list_import_drawer.sample_subtitle')}
          </div>
        </div>

        <div className={cx('detail-container')}>
          {payee?.enabled ? (
            <>
              {instantPayee?.enabled ? (
                <div className={cx('section-label')}>
                  {t(
                    'invoice_reports.list_import_drawer.registered_payee_section'
                  )}
                </div>
              ) : null}
              <div className={cx('link-container')}>
                {payee?.utf_8 && (
                  <LinkButton
                    href={payee?.utf_8}
                    isTransparent
                    rightIcon={<IconDownload />}
                  >
                    {t('invoice_reports.csv_import_drawer.detail.utf_8')}
                  </LinkButton>
                )}
                {payee?.shift_jis && (
                  <LinkButton
                    href={payee?.shift_jis}
                    isTransparent
                    rightIcon={<IconDownload />}
                  >
                    {t('invoice_reports.csv_import_drawer.detail.shift_jis')}
                  </LinkButton>
                )}
              </div>
            </>
          ) : null}
          {instantPayee?.enabled ? (
            <>
              <div className={cx('section-label')}>
                {t('invoice_reports.list_import_drawer.spot_payment_section')}
              </div>
              <div className={cx('link-container')}>
                {instantPayee?.utf_8 && (
                  <LinkButton
                    href={instantPayee?.utf_8}
                    isTransparent
                    rightIcon={<IconDownload />}
                  >
                    {t('invoice_reports.csv_import_drawer.detail.utf_8')}
                  </LinkButton>
                )}
                {instantPayee?.shift_jis && (
                  <LinkButton
                    href={instantPayee?.shift_jis}
                    isTransparent
                    rightIcon={<IconDownload />}
                  >
                    {t('invoice_reports.csv_import_drawer.detail.shift_jis')}
                  </LinkButton>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className={cx('support-link-container')}>
          <a
            href={PAYMENT_REQUEST_IMPORT_SUPPORT_URL}
            target='_blank'
            rel='noopener noreferrer'
            className={cx('link')}
          >
            {t('invoice_reports.csv_import_drawer.detail.support_link_text')}
            <div className={cx('icon')}>
              <IconExternalLink />
            </div>
          </a>
        </div>
      </div>
    );
  }
);

DetailDrawerContent.displayName = 'DetailDrawerContent';
